import { useEffect, useState } from 'react';

type Outcome = 'succeeded' | 'pending' | 'canceled' | 'failed';

interface CheckoutItem {
  name: string;
  quantity: number;
  price: number;
}

interface SessionStatus {
  status: Outcome;
  subtotalCents: number | null;
  taxCents: number | null;
  convenienceFeeCents: number | null;
  tipCents: number | null;
  totalCents: number | null;
  items: CheckoutItem[];
  cardBrand: string | null;
  cardLast4: string | null;
  receiptUrl: string | null;
  transactionIdShort: string | null;
  paidAt: number | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatBrand(brand: string | null): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatDateTime(ms: number | null): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString();
}

async function fetchStatus(sessionId: string): Promise<SessionStatus> {
  const res = await fetch(`${API_BASE_URL}/public/checkout-session/${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
  return res.json();
}

interface Props {
  variant: 'success' | 'cancel';
}

export function CheckoutResult({ variant }: Props) {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id') || '';

  const [data, setData] = useState<SessionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session id');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer: any;

    const tick = async (attempt: number) => {
      try {
        const status = await fetchStatus(sessionId);
        if (cancelled) return;
        setData(status);
        setLoading(false);
        if (status.status === 'pending' && variant === 'success' && attempt < 20) {
          timer = setTimeout(() => tick(attempt + 1), 1500);
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message || 'Could not load payment status');
        setLoading(false);
      }
    };

    tick(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, variant]);

  const effectiveStatus: Outcome =
    variant === 'cancel' ? 'canceled' : data?.status ?? (loading ? 'pending' : 'failed');

  const palette = (() => {
    switch (effectiveStatus) {
      case 'succeeded':
        return { bg: '#DBEAFE', fg: '#1d4ed8', icon: '✓', title: 'Payment successful', sub: 'Thanks — your payment went through.' };
      case 'canceled':
        return { bg: '#FEE2E2', fg: '#DC2626', icon: '✕', title: 'Payment canceled', sub: 'You canceled before completing the payment.' };
      case 'failed':
        return { bg: '#FEE2E2', fg: '#DC2626', icon: '!', title: 'Payment failed', sub: 'Your payment did not go through.' };
      case 'pending':
      default:
        return { bg: '#F3F4F6', fg: '#374151', icon: '…', title: 'Confirming payment', sub: 'Hang tight — finishing up.' };
    }
  })();

  const showDetails = effectiveStatus === 'succeeded' && data;
  const hasBreakdown =
    showDetails &&
    ((data!.taxCents ?? 0) > 0 ||
      (data!.convenienceFeeCents ?? 0) > 0 ||
      (data!.tipCents ?? 0) > 0);

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ ...iconBubbleStyle, background: palette.bg, color: palette.fg }} aria-hidden>
          {palette.icon}
        </div>
        <div style={titleStyle}>{palette.title}</div>
        <div style={subStyle}>{palette.sub}</div>

        {showDetails && data!.totalCents != null && (
          <div style={amountHeadlineStyle}>
            <span style={{ color: palette.fg }}>{formatCurrency(data!.totalCents)}</span>
          </div>
        )}

        {error && <div style={errorBoxStyle}>{error}</div>}

        {showDetails && data!.items.length > 0 && (
          <Section title="Order">
            {data!.items.map((it, i) => (
              <div key={i} style={itemRowStyle}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                  <span style={{ color: '#6B7280', minWidth: 24, textAlign: 'right' }}>{it.quantity}×</span>
                  <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name}
                  </span>
                </div>
                <span style={{ color: '#111827', fontWeight: 500 }}>
                  {formatCurrency(it.price * it.quantity)}
                </span>
              </div>
            ))}
          </Section>
        )}

        {showDetails && hasBreakdown && (
          <Section title="Breakdown">
            {data!.subtotalCents != null && (
              <Row label="Subtotal" value={formatCurrency(data!.subtotalCents)} />
            )}
            {(data!.taxCents ?? 0) > 0 && <Row label="Tax" value={formatCurrency(data!.taxCents)} />}
            {(data!.convenienceFeeCents ?? 0) > 0 && (
              <Row label="Convenience Fee (3%)" value={formatCurrency(data!.convenienceFeeCents)} />
            )}
            {(data!.tipCents ?? 0) > 0 && <Row label="Tip" value={formatCurrency(data!.tipCents)} />}
            {data!.totalCents != null && (
              <Row label="Total" value={formatCurrency(data!.totalCents)} accent={palette.fg} strong />
            )}
          </Section>
        )}

        {showDetails && (data!.cardBrand || data!.cardLast4 || data!.transactionIdShort || data!.paidAt) && (
          <Section title="Details">
            {(data!.cardBrand || data!.cardLast4) && (
              <Row
                label="Paid with"
                value={`${formatBrand(data!.cardBrand)}${data!.cardLast4 ? ` •••• ${data!.cardLast4}` : ''}`}
              />
            )}
            {data!.paidAt && <Row label="Date" value={formatDateTime(data!.paidAt)} />}
            {data!.transactionIdShort && (
              <Row
                label="Reference"
                value={data!.transactionIdShort}
                mono
              />
            )}
          </Section>
        )}

        {showDetails && data!.receiptUrl && (
          <a href={data!.receiptUrl} target="_blank" rel="noopener noreferrer" style={receiptBtnStyle}>
            View Stripe receipt
          </a>
        )}

        <div style={footerNoteStyle}>You can close this tab. The cashier has been notified.</div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  accent,
  strong,
  mono,
}: {
  label: string;
  value: string;
  accent?: string;
  strong?: boolean;
  mono?: boolean;
}) {
  return (
    <div style={rowStyle}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      <span
        style={{
          fontWeight: strong ? 700 : 600,
          color: accent || '#111827',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
          fontSize: mono ? 13 : undefined,
        }}>
        {value}
      </span>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  background: '#F9FAFB',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  background: '#FFFFFF',
  borderRadius: 20,
  boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
  padding: 28,
  textAlign: 'center',
  border: '1px solid #E5E7EB',
};

const iconBubbleStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 32,
  fontWeight: 700,
  margin: '0 auto 14px',
};

const titleStyle: React.CSSProperties = { fontSize: 20, fontWeight: 700, color: '#111827' };
const subStyle: React.CSSProperties = { fontSize: 14, color: '#6B7280', marginTop: 6 };

const amountHeadlineStyle: React.CSSProperties = {
  marginTop: 16,
  fontSize: 32,
  fontWeight: 700,
  letterSpacing: '-0.02em',
};

const sectionStyle: React.CSSProperties = {
  marginTop: 18,
  padding: 14,
  background: '#F9FAFB',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  textAlign: 'left',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 8,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '6px 0',
  fontSize: 14,
  gap: 12,
};

const itemRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '6px 0',
  fontSize: 14,
  gap: 12,
};

const errorBoxStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 10,
  background: '#FEE2E2',
  color: '#DC2626',
  borderRadius: 10,
  fontSize: 13,
};

const receiptBtnStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 18,
  padding: '12px 16px',
  background: '#FFFFFF',
  color: '#1d4ed8',
  border: '1px solid #1d4ed8',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 600,
  textDecoration: 'none',
  textAlign: 'center',
};

const footerNoteStyle: React.CSSProperties = {
  marginTop: 22,
  fontSize: 12,
  color: '#9CA3AF',
};
